import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { CookieConsent } from './components/ui/CookieConsent';
import { FloatingWhatsApp } from './components/ui/FloatingWhatsApp';
import { useStore } from './store';
import { supabase } from './lib/supabase';
import { Session } from '@supabase/supabase-js';
import { authService } from './services/api/auth';
import { Loader2 } from 'lucide-react';
import { NotificationProvider } from './components/ui/Notification';
import { Dialog } from './components/ui/Dialog';

// Lazy load pages to reduce initial bundle size
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const UnitProfile = React.lazy(() => import('./pages/UnitProfile'));
const PPDB = React.lazy(() => import('./pages/PPDB'));
const PPDBRegister = React.lazy(() => import('./pages/PPDBRegister'));
const Scholarships = React.lazy(() => import('./pages/Scholarships'));
const ScholarshipRegister = React.lazy(() => import('./pages/ScholarshipRegister'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const CompleteProfile = React.lazy(() => import('./pages/CompleteProfile'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Career = React.lazy(() => import('./pages/Career'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const Profile = React.lazy(() => import('./pages/Profile'));
const RegistrationProof = React.lazy(() => import('./pages/RegistrationProof'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const NewsPage = React.lazy(() => import('./pages/News'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App = () => {
  const { pathname } = useLocation();
  // Use selectors to avoid unnecessary re-renders
  const login = useStore((state) => state.login);
  const logout = useStore((state) => state.logout);
  const user = useStore((state) => state.user);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showInactiveAccountDialog, setShowInactiveAccountDialog] = useState(false);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // 1. Cek session lokal dengan timeout sangat pendek (200ms)
        // Ini penting untuk mengatasi masalah "Clock Skew" dimana getSession() bisa hang/lama
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve({ timeout: true }), 200),
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result: any = await Promise.race([sessionPromise, timeoutPromise]);
        let session = result?.data?.session;

        // 2. Jika timeout, error, atau tidak ada session, coba verifikasi langsung ke server
        // Only call getUser if there's a stored token to avoid unauthenticated requests
        if (result?.timeout || result?.error || !session) {
          try {
            const tokenKey = 'supabase.auth.token';
            const storedToken =
              typeof localStorage !== 'undefined' ? localStorage.getItem(tokenKey) : null;
            if (storedToken) {
              const {
                data: { user },
                error: userError,
              } = await supabase.auth.getUser();
              if (user && !userError) {
                session = { user } as unknown as Session;
              }
            }
          } catch {
            // Silent
          }
        }

        if (session?.user && mounted) {
          try {
            const user = await authService.getCurrentUser(session.user);
            if (user && mounted) login(user);
          } catch (err) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const error = err as any;
            const status =
              error?.status ||
              error?.statusCode ||
              (error?.originalError && error.originalError.status);
            const msg = String(error?.message || '');
            if (
              status === 401 ||
              status === 403 ||
              msg.includes('401') ||
              msg.includes('403') ||
              msg.toLowerCase().includes('unauthorized')
            ) {
              // Expected: no active session / unauthorized — treat silently for UX
              console.debug('Auth: user unauthenticated during init (expected).', {
                status,
                message: msg,
              });
            } else {
              console.error('User authentication failed:', error);
            }
            // If user is inactive, sign them out
            if (error instanceof Error && error.message.includes('dinonaktifkan')) {
              await supabase.auth.signOut();
              setShowInactiveAccountDialog(true);
            }
          }
        }
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err = e as any;
        const status =
          err?.status || err?.statusCode || (err?.originalError && err.originalError.status);
        const msg = String(err?.message || '');
        if (
          status === 401 ||
          status === 403 ||
          msg.includes('401') ||
          msg.includes('403') ||
          msg.toLowerCase().includes('unauthorized')
        ) {
          console.debug('Auth init: unauthenticated (expected).', { status, message: msg });
        } else {
          console.error('Auth init failed:', err);
        }
      } finally {
        if (mounted) setIsAuthChecking(false);
      }
    };
    initializeAuth();

    // Listener untuk perubahan auth (Login/Logout/Refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (session?.user) {
          // Cek apakah user sudah ada di state untuk mencegah loop
          const currentUser = useStore.getState().user;
          if (currentUser?.id === session.user.id) return;

          try {
            const user = await authService.getCurrentUser(session.user);
            if (user && mounted) login(user);
          } catch (err) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const error = err as any;
            const status =
              error?.status ||
              error?.statusCode ||
              (error?.originalError && error.originalError.status);
            const msg = String(error?.message || '');
            if (
              status === 401 ||
              status === 403 ||
              msg.includes('401') ||
              msg.includes('403') ||
              msg.toLowerCase().includes('unauthorized')
            ) {
              console.debug('Auth: unauthenticated on auth state change (expected).', {
                status,
                message: msg,
              });
            } else {
              console.error('User authentication failed:', error);
            }
            // If user is inactive, sign them out
            if (error instanceof Error && error.message.includes('dinonaktifkan')) {
              await supabase.auth.signOut();
              setShowInactiveAccountDialog(true);
            }
          }
        }
      } else if (event === 'SIGNED_OUT') {
        logout();
      }

      // Pastikan loader mati setelah event apapun
      setIsAuthChecking(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Hide Navbar/Footer on Admin Dashboard and 404 page (optional logic for 404, usually better to keep nav)
  // Let's keep Nav/Footer for 404 to help user navigate back, but hide for Admin
  const isAdminRoute = pathname.startsWith('/admin');

  if (isAuthChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <NotificationProvider>
      <div className="flex flex-col min-h-screen bg-slate-50 font-sans">
        <ScrollToTop />
        {!isAdminRoute && <Navbar />}
        <main className="grow">
          <React.Suspense
            fallback={
              <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/unit/:slug" element={<UnitProfile />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<BlogPost />} />

              {/* Public Landing Page for SPMB */}
              <Route path="/spmb" element={<PPDB />} />

              {/* Protected Registration Form */}
              <Route
                path="/spmb/register"
                element={
                  <ProtectedRoute>
                    <PPDBRegister />
                  </ProtectedRoute>
                }
              />

              <Route path="/scholarships" element={<Scholarships />} />
              <Route
                path="/scholarship-register"
                element={
                  <ProtectedRoute>
                    <ScholarshipRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              <Route path="/faq" element={<FAQ />} />
              <Route path="/career" element={<Career />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/complete-profile"
                element={
                  <ProtectedRoute>
                    <CompleteProfile />
                  </ProtectedRoute>
                }
              />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route
                path="/registration-proof"
                element={
                  <ProtectedRoute>
                    <RegistrationProof />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin" element={<AdminDashboard />} />

              {/* Legal Pages */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* Fallback 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </main>
        {!isAdminRoute && <Footer />}

        {/* Global Components */}
        <CookieConsent />
        {/* Hide Floating WhatsApp on admin routes, dashboard, spmb register, and for admin users */}
        {(() => {
          const isAdminUser = Boolean(
            user?.role &&
            (String(user.role) === 'super_admin' || String(user.role).startsWith('admin')),
          );
          const isAuthPage = pathname === '/login' || pathname === '/register';
          const shouldShowFloatingWhatsApp =
            !isAdminRoute &&
            !isAdminUser &&
            pathname !== '/dashboard' &&
            pathname !== '/spmb/register' &&
            !(isAuthPage && isMobile);

          return shouldShowFloatingWhatsApp && <FloatingWhatsApp />;
        })()}

        {/* Inactive Account Dialog */}
        <Dialog
          isOpen={showInactiveAccountDialog}
          onClose={() => setShowInactiveAccountDialog(false)}
          title="Akun Dinonaktifkan"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">Akun Anda Telah Dinonaktifkan</h3>
                <p className="text-sm text-red-700 mt-1">
                  Akun Anda telah dinonaktifkan oleh administrator. Silakan hubungi administrator
                  untuk mengaktifkan kembali akun Anda.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowInactiveAccountDialog(false)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
              >
                Tutup
              </button>
            </div>
          </div>
        </Dialog>
      </div>
    </NotificationProvider>
  );
};

export default App;
